/**
 * Title screen — new game / continue, difficulty selection, and the pitch.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { BedDouble, Play, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGameStore } from '@/store/gameStore';
import { DIFFICULTIES } from '@/game/constants';
import type { Difficulty } from '@/game/types';
import { hasSave } from '@/game/save';
import { MattressArt } from '@/components/game/art/MattressArt';
import { MascotMoon, Zzz } from '@/components/game/art/Scenes';

const DIFFICULTY_ORDER: Difficulty[] = ['easy', 'normal', 'hard', 'realistic'];

export default function Index() {
  useSeoMeta({
    title: 'Mattress Empire — Build a Bed-in-a-Box Empire',
    description:
      'Found a DTC mattress brand, survive tariffs and oil shocks, outmarket 175 rivals, and exit rich (or die broke). A realistic tycoon game for browser and iPhone.',
  });

  const navigate = useNavigate();
  const newGame = useGameStore((s) => s.newGame);
  const continueGame = useGameStore((s) => s.continueGame);
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('normal');

  const startGame = () => {
    newGame(companyName || 'DreamCo', difficulty);
    navigate('/play');
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-950/60 via-background to-background" />
        <div className="absolute -top-32 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl motion-safe:animate-pulse" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-4xl flex-col items-center justify-center px-4 py-16 text-center">
        <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-lg">
          <BedDouble className="h-10 w-10" strokeWidth={1.5} />
        </div>

        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-6xl">
          Mattress <span className="text-primary">Empire</span>
        </h1>
        <p className="mt-4 max-w-xl text-lg text-muted-foreground">
          You have one SKU, a Shopify store, an Amazon seller account, and{' '}
          <span className="font-medium text-foreground">175 competitors</span> who want you dead.
          Survive the tariffs. Ride the oil shocks. Master the CAC game.
          <span className="font-medium text-foreground"> Build the empire.</span>
        </p>

        {/* Hero illustration: the flagship on a pedestal, Luna napping above */}
        <div className="relative my-8 w-full max-w-md">
          <MattressArt archetype="luxury" quality={88} coolingScore={84} seed={7} className="met-bob w-full" />
          <MascotMoon className="absolute -top-12 right-0 w-16 sm:-right-8 sm:w-20" />
          <svg viewBox="0 0 60 60" className="absolute -top-14 right-16 w-12 sm:right-24" aria-hidden>
            <Zzz />
          </svg>
          <div className="mx-auto mt-1 h-2 w-3/4 rounded-full bg-black/20 blur-[2px]" aria-hidden />
        </div>

        <div className="mt-8 flex w-full max-w-xs flex-col gap-3">
          <Button size="lg" onClick={() => setOpen(true)} className="h-12 text-base">
            <Plus className="mr-2 h-5 w-5" /> Found your company
          </Button>
          <Button
            size="lg"
            variant="outline"
            disabled={!hasSave()}
            className="h-12 text-base"
            onClick={() => {
              if (continueGame()) navigate('/play');
            }}
          >
            <Play className="mr-2 h-5 w-5" /> Continue empire
          </Button>
        </div>

        <ul className="mt-12 grid gap-3 text-left text-sm text-muted-foreground sm:grid-cols-3">
          <li className="rounded-xl border bg-card/80 p-4">
            <p className="mb-1 font-medium text-foreground">Real economics</p>
            Landed costs, AD/CVD duties up to +92%, 8–15% return rates, fuel surcharges that lag oil by weeks — modeled on the actual bed-in-a-box wars.
          </li>
          <li className="rounded-xl border bg-card/80 p-4">
            <p className="mb-1 font-medium text-foreground">30+ risk events</p>
            Sunset reviews, port strikes, viral return exposés, Amazon fee hikes. Every event demands a real decision with real trade-offs.
          </li>
          <li className="rounded-xl border bg-card/80 p-4">
            <p className="mb-1 font-medium text-foreground">Play anywhere</p>
            Installable PWA for your iPhone home screen. Offline-capable, autosaves weekly, simulates days while you're away.
          </li>
        </ul>
      </div>

      {/* New game dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Found your mattress company</DialogTitle>
            <DialogDescription>Name it. Pick your pain level. The market does not care about your feelings.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="company">Company name</Label>
              <Input
                id="company"
                placeholder='e.g. "DreamCo"'
                value={companyName}
                maxLength={32}
                onChange={(e) => setCompanyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && startGame()}
              />
            </div>

            <div className="space-y-2">
              <Label>Difficulty</Label>
              <div className="grid grid-cols-2 gap-2">
                {DIFFICULTY_ORDER.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    aria-pressed={difficulty === d}
                    className={cn(
                      'rounded-xl border p-3 text-left transition-colors hover:border-primary/60',
                      difficulty === d && 'border-primary ring-1 ring-primary',
                    )}
                  >
                    <p className="text-sm font-medium">{DIFFICULTIES[d].label}</p>
                    <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{DIFFICULTIES[d].blurb}</p>
                    <p className="mt-1 text-xs tabular-nums text-muted-foreground">${(DIFFICULTIES[d].startingCash / 1000).toFixed(0)}k starting cash</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button size="lg" className="w-full" onClick={startGame}>
              Launch {companyName.trim() || 'DreamCo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
